import React, { useState, createContext, useEffect } from 'react';
import useLocalStorage from '../hooks/useLocalStorage'; // Importa tu hook personalizado
import axios from 'axios';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    // Usar el hook personalizado para manejar el token
    const [authToken, setAuthToken] = useLocalStorage("token", null);
    const [isAuth, setIsAuth] = useState(() => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            return !!refreshToken;  // Devuelve true si hay refreshToken, false si no
        } catch (error) {
            return false;
        }
    });

    const [StatusDocument, setStatusDocument] = useState(null);
    const [typePerson, setPerson] = useState(null);
    const [DetailUser, setDetailUser] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false); // Estado para controlar si se está refrescando el token
    const [refreshQueue, setRefreshQueue] = useState([]); // Cola para almacenar solicitudes pendientes

    // Monitorear cambios en authToken y actualizar isAuth
    useEffect(() => {
        if (authToken) {
            setIsAuth(true);  // Si hay authToken, usuario está autenticado
        } else {
            setIsAuth(false);  // Si no hay authToken, el usuario no está autenticado
        }
    }, [authToken]);

    // Función para cerrar sesión y eliminar los tokens
    const logout = () => {
        setPerson(null);
        setStatusDocument(null);

        try {
            // Elimina los tokens del localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('expiresAtC');
            localStorage.removeItem("paso");
            localStorage.removeItem("userDetails");

            // Elimina el token del hook personalizado
            setAuthToken(null);

            // Cambia el estado de autenticación
            setIsAuth(false);
        } catch (error) {
            console.error("Error al cerrar sesión", error);
        }
    };

    const fetchUserDetails = async () => {
        try {
            const response = await axios.post(`/api/user`, {
                headers: await getAuthenticatedHeaders(),
            });

            if (response?.data?.data && typeof response.data.data === 'object') {
                localStorage.setItem('userDetails', JSON.stringify(response.data.data));
                setDetailUser(response.data.data);
            } else {
                console.warn("Datos de usuario inválidos, no se guardarán en localStorage.");
            }
        } catch (error) {
            console.error("Error al obtener los datos del usuario:", error);
        }
    };

    const getAccessToken = async () => {
        try {
            // Obtener el token actual
            const token = authToken;

            // Verificar si el token existe
            if (!token) {
                setIsAuth(false); // No hay token, el usuario no está autenticado
                return null;
            }

            // Obtener la fecha de creación del token
            const tokenCreated = localStorage.getItem('expiresAtC');

            // Verificar si la fecha de creación del token existe
            if (!tokenCreated) {
                console.error("No se encontró la fecha de creación del token.");
                setIsAuth(false);
                return null;
            }

            // Calcular el tiempo transcurrido desde la creación del token
            const currentTime = new Date();
            const tokenCreationTime = new Date(Date.parse(tokenCreated));
            const minutesSinceCreation = DifferenceMinutes(currentTime, tokenCreationTime);

            // Verificar si el token ha expirado (5 minutos o más)
            if (minutesSinceCreation > 1) {
                if (isRefreshing) {
                    // Si ya se está refrescando el token, agregar la solicitud a la cola
                    return new Promise((resolve, reject) => {
                        refreshQueue.push({ resolve, reject });
                    });
                } else {
                    // Si no se está refrescando, iniciar el proceso de refresh
                    setIsRefreshing(true);
                    const newToken = await refreshAccessToken();
                    setIsRefreshing(false);

                    if (newToken) {
                        setIsAuth(true);
                        // Resolver todas las solicitudes en la cola con el nuevo token
                        refreshQueue.forEach(({ resolve }) => resolve(newToken));
                        setRefreshQueue([]); // Limpiar la cola
                        return newToken;
                    } else {
                        setIsAuth(false);
                        // Rechazar todas las solicitudes en la cola
                        refreshQueue.forEach(({ reject }) => reject(new Error("Failed to refresh token")));
                        setRefreshQueue([]); // Limpiar la cola
                        return null;
                    }
                }
            } else {
                // El token aún es válido
                setIsAuth(true);
                return token;
            }
        } catch (error) {
            console.error("Error al obtener el token de acceso:", error);
            setIsAuth(false); // Hubo un error, el usuario no está autenticado
            return null;
        }
    };

    const DifferenceMinutes = (dt2, dt1) => {
        let diff = (dt2.getTime() - dt1.getTime()) / 1000;
        diff /= 60;
        return Math.abs(Math.round(diff));
    };

    const refreshAccessToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const response = await axios.post(`/api/token/refresh`, { refreshToken });

            if (response.data.access) {
                localStorage.setItem('token', response.data.access);
                localStorage.setItem('expiresAtC', new Date().toString());
                setAuthToken(response.data.access); // Aquí se guarda el nuevo token
                return response.data.access;
            } else {
                setIsAuth(false);
                return null;
            }
        } catch (error) {
            setIsAuth(false);
            return null;
        }
    };

    const getAuthenticatedHeaders = async () => {
        try {
            const access_token = await getAccessToken();

            return {
                Authorization: `Bearer ${access_token}`,
            };
        } catch (error) {
            console.error("Error al obtener headers autenticados", error);
        }
    };

    const value = {
        getAuthenticatedHeaders: (x) => getAuthenticatedHeaders(x),
        setAuthToken,
        authToken,
        isAuth,
        logout,
        StatusDocument,
        setStatusDocument,
        typePerson,
        setPerson,
        DetailUser,
        setDetailUser,
        fetchUserDetails
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;